import { useRef, useState, type ReactNode } from "react";
import type {
  ProposalCollaborationEnvelope,
  ProposalPublicationSelection,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import type {
  ProposalChangeDescriptionContext,
  ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import { describeProposalChangeLine } from "work/proposals/negotiation/proposal-change-descriptors";
import {
  proposalNegotiationRevisesWorkAgreement,
  proposalPublishableChanges,
  proposalSelectiveTranchePublicationApplies,
} from "work/proposals/negotiation/proposal-publishable-changes";
import { proposalSubmissionSummaryHeading } from "work/proposals/_views/_shared/proposal-submission-summary";
import {
  isInitialProposalSubmission,
  proposalSubmitAudienceLabel,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import { proposalPendingReviewWarning } from "work/proposals/_views/_shared/proposal-pending-review-warning";
import SubmitProposalDialog, {
  type SubmitProposalDialogResult,
} from "work/proposals/_views/_shared/submit-proposal-dialog";

/**
 * The submission decision, asked once.
 *
 * Callers save first (so the change list is the authoritative post-save one), then call
 * `resolveSubmission` with the acknowledged envelope. The dialog names every change, says who receives
 * it, and offers explicit restoration to the prior submitted value — replacing the old
 * confirm-then-maybe-a-second-dialog sequence. Comments are not its subject: they are published as they
 * are posted.
 */

export const CANCELED_SUBMISSION = "canceledSubmission" as const;
export type CanceledSubmission = typeof CANCELED_SUBMISSION;

export type ResolvedSubmission =
  | CanceledSubmission
  | {
      revertSelection: readonly ProposalPublicationSelection[];
    };

export type SubmitProposalDialogRequest = {
  envelope: ProposalCollaborationEnvelope;
  title: string;
  confirmButtonText: string;
  context?: ProposalChangeDescriptionContext;
};

export type SubmitProposalDialogController = {
  resolveSubmission: (request: SubmitProposalDialogRequest) => Promise<ResolvedSubmission>;
  submitProposalDialog: ReactNode;
};

type SubmitPrompt = {
  title: string;
  confirmButtonText: string;
  message?: string;
  changes: ProposalChangeDescriptor[];
  audienceLabel: string;
  changesHeading: string;
  allowRevert: boolean;
};

export default function useSubmitProposalDialog(): SubmitProposalDialogController {
  const [prompt, setPrompt] = useState<SubmitPrompt>();
  const resolverRef = useRef<((result: SubmitProposalDialogResult | undefined) => void) | undefined>(undefined);

  async function resolveSubmission(request: SubmitProposalDialogRequest): Promise<ResolvedSubmission> {
    const { envelope, context } = request;
    // Selective publication only makes sense once something has already been published; a first
    // submission sends the whole proposal, so the list is informational rather than a choice.
    const allowRevert = proposalSelectiveTranchePublicationApplies(envelope);
    // The envelope's own line, not a stand-in built from the parts of it this dialog happened to need.
    // The stand-in dropped the one thing only the line carries — the value the counterparty can still
    // see — so this dialog showed a bare new value where the save confirmation and the field history
    // both drew the transition, which is the very divergence the shared change vocabulary exists to
    // prevent.
    //
    // A genuine first submission has nothing behind it to diff against, so the list would only
    // itemize every populated field as a "change" — noise the leader just typed themselves (#4952).
    // The dialog then shows the plain confirmation alone.
    const changes = isInitialProposalSubmission(envelope)
      ? []
      : proposalPublishableChanges(envelope).map((change) =>
          describeProposalChangeLine(envelope, change.line, context ?? {}),
        );

    // Derived here rather than asked of the caller: both submit paths owe the leader the same warning,
    // and only the acknowledged envelope knows which approvals survived the save that just ran.
    const audienceLabel = proposalSubmitAudienceLabel(envelope.viewingParty);

    const result = await new Promise<SubmitProposalDialogResult | undefined>((resolve) => {
      resolverRef.current = resolve;
      setPrompt({
        title: request.title,
        confirmButtonText: request.confirmButtonText,
        message: proposalPendingReviewWarning(envelope, audienceLabel),
        changes,
        audienceLabel,
        // Two headings for the two lists still shown: a delta since the last submission, or a
        // revision's delta against the live work agreement. Meaningless (and unused, since `changes`
        // is empty) on a genuine first submission, which shows no list at all.
        changesHeading: proposalSubmissionSummaryHeading(
          allowRevert,
          proposalNegotiationRevisesWorkAgreement(envelope),
        ),
        allowRevert,
      });
    });

    if (!result) return CANCELED_SUBMISSION;
    return {
      revertSelection: result.revertedChanges.map(toPublicationSelection),
    };
  }

  function closeDialog(result: SubmitProposalDialogResult | undefined) {
    setPrompt(undefined);
    resolverRef.current?.(result);
    resolverRef.current = undefined;
  }

  return {
    resolveSubmission,
    submitProposalDialog: prompt ? (
      <SubmitProposalDialog
        title={prompt.title}
        changes={prompt.changes}
        audienceLabel={prompt.audienceLabel}
        changesHeading={prompt.changesHeading}
        allowRevert={prompt.allowRevert}
        message={prompt.message}
        confirmButtonText={prompt.confirmButtonText}
        onSubmit={(result) => closeDialog(result)}
        onCancel={() => closeDialog(undefined)}
      />
    ) : null,
  };
}

/**
 * The wire shape is keyed by conflict key plus item; the descriptor's own `key` packs both, so it is
 * split back apart here rather than carrying a second identity through the dialog.
 */
function toPublicationSelection(change: ProposalChangeDescriptor): ProposalPublicationSelection {
  const [conflictKey, itemId] = change.key.split("|");
  return itemId ? { conflictKey, itemId } : { conflictKey };
}
